import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getTokenSymbol, isNativeETH } from '../utils/helpers';

const PriceContainer = styled.div`
  margin-top: 4px;
  font-size: 0.75rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 8px;
  transition: color 0.3s ease;
`;

const PriceItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PriceValue = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  transition: color 0.3s ease;
`;

interface InlinePriceDisplayProps {
  amount: number;
  currency: 'ETH' | 'TOKEN';
  ethPrice?: number;
  tokenPrice?: number;
  isPriceLoading?: boolean;
  action?: 'buy' | 'sell';
  quoteTokenAddress?: string;
}

const InlinePriceDisplay: React.FC<InlinePriceDisplayProps> = ({
  amount,
  currency,
  ethPrice = 3000,
  tokenPrice = 0.1,
  isPriceLoading = false,
  action = 'buy',
  quoteTokenAddress
}) => {
  const [usdValue, setUsdValue] = useState<number>(0);
  
  useEffect(() => {
    if (amount > 0) {
      if (currency === 'ETH') {
        // Для ETH: количество * цена ETH
        setUsdValue(amount * ethPrice);
      } else {
        // Для TOKEN: определяем правильную цену в зависимости от action
        if (action === 'buy') {
          // При покупке: пользователь вводит количество quote token (USDC, WETH и т.д.)
          // Для USDC цена всегда 1
          const isUSDC = quoteTokenAddress?.toLowerCase() === '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913' ||
                         quoteTokenAddress?.toLowerCase() === '0xd9aaec86b65d86f6a7b5b1b0c42ffa531710b6ca';
          const quotePrice = isUSDC ? 1 : ethPrice; // Для USDC = 1, для других quote токенов используем ethPrice
          setUsdValue(amount * quotePrice);
        } else {
          // При продаже: пользователь вводит количество целевого токена
          setUsdValue(amount * tokenPrice);
        }
      }
    } else {
      setUsdValue(0);
    }
  }, [amount, currency, ethPrice, tokenPrice, action, quoteTokenAddress]);

  // Всегда отображаем компонент, даже если amount = 0
  if (amount <= 0) {
    return (
      <PriceContainer>
        <PriceItem>
          💵 USD: <PriceValue>$0.00</PriceValue>
        </PriceItem>
        {currency === 'ETH' && (
          <PriceItem>
            📈 ETH: <PriceValue>${ethPrice.toFixed(2)}</PriceValue>
          </PriceItem>
        )}
        {currency === 'TOKEN' && (
          <PriceItem>
            🪙 {action === 'buy' ? getTokenSymbol(quoteTokenAddress) : 'Token'}: <PriceValue>${(action === 'buy' && quoteTokenAddress && (quoteTokenAddress.toLowerCase() === '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913' || quoteTokenAddress.toLowerCase() === '0xd9aaec86b65d86f6a7b5b1b0c42ffa531710b6ca') ? 1 : (action === 'buy' ? ethPrice : tokenPrice)).toFixed(action === 'buy' && quoteTokenAddress && (quoteTokenAddress.toLowerCase() === '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913' || quoteTokenAddress.toLowerCase() === '0xd9aaec86b65d86f6a7b5b1b0c42ffa531710b6ca') ? 2 : 6)}</PriceValue>
          </PriceItem>
        )}
      </PriceContainer>
    );
  }

  // Определяем правильную цену для отображения
  const displayPrice = currency === 'ETH' 
    ? ethPrice 
    : (action === 'buy' 
      ? (quoteTokenAddress && (quoteTokenAddress.toLowerCase() === '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913' || quoteTokenAddress.toLowerCase() === '0xd9aaec86b65d86f6a7b5b1b0c42ffa531710b6ca') 
        ? 1 
        : (isNativeETH(quoteTokenAddress) ? ethPrice : ethPrice))
      : tokenPrice);
  
  const displaySymbol = currency === 'ETH' 
    ? 'ETH' 
    : (action === 'buy' 
      ? getTokenSymbol(quoteTokenAddress)
      : 'Token');

  if (isPriceLoading) {
    return (
      <PriceContainer>
        <PriceItem>
          💵 USD: <PriceValue>Loading...</PriceValue>
        </PriceItem>
        {currency === 'ETH' && (
          <PriceItem>
            📈 ETH: <PriceValue>Loading...</PriceValue>
          </PriceItem>
        )}
        {currency === 'TOKEN' && (
          <PriceItem>
            🪙 {displaySymbol}: <PriceValue>Loading...</PriceValue>
          </PriceItem>
        )}
      </PriceContainer>
    );
  }

  return (
    <PriceContainer>
      <PriceItem>
        💵 USD: <PriceValue>${usdValue.toFixed(2)}</PriceValue>
      </PriceItem>
      {currency === 'ETH' && (
        <PriceItem>
          📈 ETH: <PriceValue>${ethPrice.toFixed(2)}</PriceValue>
        </PriceItem>
      )}
      {currency === 'TOKEN' && (
        <PriceItem>
          🪙 {displaySymbol}: <PriceValue>${displayPrice.toFixed(action === 'buy' && (displaySymbol === 'USDC' || displaySymbol === 'USDbC') ? 2 : 6)}</PriceValue>
        </PriceItem>
      )}
    </PriceContainer>
  );
};

export default InlinePriceDisplay;
